// app/shared/components/layout/HTFSectionHeader.tsx
// v2
"use client";

// app/shared/components/layout/HTFSectionHeader.tsx
import { ChevronDown, ChevronUp, LucideIcon } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

export interface HTFSectionHeaderProps {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  badge?: string;
  badgeVariant?:
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "info";
  divider?: boolean;
  collapsible?: boolean;
  onCollapseToggle?: () => void;
  isCollapsed?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  iconClassName?: string;
  fullWidthLine?: boolean;
}

export function HTFSectionHeader({
  title,
  subtitle,
  size = "md",
  icon: Icon,
  badge,
  badgeVariant = "primary",
  divider = true,
  collapsible = false,
  onCollapseToggle,
  isCollapsed = false,
  className,
  titleClassName,
  subtitleClassName,
  fullWidthLine = false,
  iconClassName,
}: HTFSectionHeaderProps) {
  const sizeStyles = {
    xs: {
      icon: 16,
      title: "text-xs font-semibold uppercase tracking-wider font-mono",
    },
    sm: { icon: 20, title: "text-lg font-semibold" },
    md: { icon: 24, title: "text-xl font-bold" },
    lg: { icon: 28, title: "text-2xl font-bold" },
  };

  const selectedSize = sizeStyles[size];

  // Logic for the full-width line variant
  if (fullWidthLine) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {Icon && (
          <Icon
            size={selectedSize.icon}
            className={cn("text-gray-600", iconClassName)}
          />
        )}
        <h5
          className={cn(
            "text-gray-700 whitespace-nowrap",
            selectedSize.title,
            titleClassName,
          )}
        >
          {title}
        </h5>
        <div className="h-px flex-grow bg-gray-400" />
      </div>
    );
  }

  // Original component logic
  const getBadgeStyles = () => {
    const baseStyles = "ml-2 text-xs font-medium py-0.5 px-2 rounded-full";
    const variantStyles = {
      primary: "bg-primary text-black",
      secondary: "bg-secondary text-black",
      success: "bg-success text-white",
      warning: "bg-warning text-black",
      error: "bg-error text-white",
      info: "bg-info text-black",
    };
    return `${baseStyles} ${variantStyles[badgeVariant]}`;
  };

  return (
    <div className={cn("mb-4", className)}>
      <div className={cn("flex items-center", { "mb-1": subtitle })}>
        {Icon && (
          <Icon
            size={selectedSize.icon}
            className={cn("text-gray-700 mr-1.5", iconClassName)}
          />
        )}
        <h5
          className={cn(
            "font-semibold text-gray-800 flex items-center gap-1.5",
            selectedSize.title,
            titleClassName,
          )}
        >
          {title}
        </h5>
        {badge && <span className={getBadgeStyles()}>{badge}</span>}
        {collapsible && (
          <button
            onClick={onCollapseToggle}
            className="ml-2 text-gray-500 hover:text-gray-700 p-0.5 rounded focus:outline-none focus:ring-1 focus:ring-primary"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Expand section" : "Collapse section"}
          >
            {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </button>
        )}
        {divider && !fullWidthLine && (
          <div className="h-px flex-grow bg-gray-300 ml-3" />
        )}
      </div>
      {subtitle && (
        <p
          className={cn(
            "text-md text-gray-500",
            { "ml-6": Icon },
            subtitleClassName,
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}
